import { useEffect, useState } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { ArrowLeft } from "lucide-react";
import { Fleuron, Flourish } from "@/components/Ornaments";
import { TYPE_META, TYPE_ORDER } from "@/lib/api";

const BASE = process.env.REACT_APP_BACKEND_URL;

export default function PublicCollection() {
  const { token } = useParams();
  const [sp] = useSearchParams();
  // ?from=username when arrived from a friend's profile — lets us render a
  // labelled "Back to {Maya}'s profile" link so no screen is a dead end.
  const fromUsername = sp.get("from") || "";
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const r = await axios.get(`${BASE}/api/public/collections/${token}`);
        if (!cancel) setData(r.data);
      } catch (e) {
        console.error("[public-collection] load failed:", e);
        if (!cancel) setErr(true);
      }
    })();
    return () => { cancel = true; };
  }, [token]);

  if (err) {
    return (
      <div className="max-w-xl mx-auto text-center py-24" data-testid="public-collection-missing">
        <Fleuron size={26} color="var(--bronze)" className="mx-auto opacity-70" />
        <h1 className="mt-6 font-serif-lit text-4xl text-[var(--ink)]">Not here, friend.</h1>
        <p className="mt-3 font-garamond italic text-[var(--ink-dusty)]">
          This collection is private now, or the link has been mistyped.
        </p>
        <Link to="/" className="mt-8 inline-block font-cinzel text-[10px] tracking-[0.26em] uppercase text-[var(--bronze)]">
          Visit Curated Ethos
        </Link>
      </div>
    );
  }
  if (!data) {
    return <p className="text-center font-cinzel text-xs tracking-[0.26em] uppercase text-[var(--ink-dusty)] py-24">Loading…</p>;
  }

  const byType = {};
  for (const it of data.items) (byType[it.type] ||= []).push(it);

  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="space-y-12" data-testid="public-collection">
      {/* Back-breadcrumb — the way home. If arrived from a friend's profile,
          label the back-link so the user never ends up in a dead end. */}
      {(fromUsername || data.owner?.username) && (
        <nav className="flex flex-col gap-2" data-testid="public-collection-nav" aria-label="Breadcrumb">
          <Link
            to={`/u/${encodeURIComponent(fromUsername || data.owner.username)}`}
            data-testid="public-collection-back"
            className="inline-flex items-center gap-2 font-cinzel text-[10px] tracking-[0.26em] uppercase text-[var(--ink-dusty)] hover:text-[var(--bronze)] self-start"
          >
            <ArrowLeft size={12} strokeWidth={1.5}/>
            Back to {data.owner?.name || `@${fromUsername || data.owner.username}`}&apos;s profile
          </Link>
          <p className="font-mono-lc text-[10px] tracking-[0.14em] text-[var(--ink-light)]" data-testid="public-collection-breadcrumb">
            <Link to={`/u/${encodeURIComponent(fromUsername || data.owner.username)}`} className="hover:text-[var(--bronze)]">
              {data.owner?.name || fromUsername}
            </Link>
            <span className="mx-2 opacity-50">&gt;</span>
            <span className="text-[var(--ink-dusty)]">{data.name}</span>
          </p>
        </nav>
      )}

      {/* Owner avatar block — tappable, goes straight to their profile */}
      {data.owner?.username && (
        <Link
          to={`/u/${encodeURIComponent(data.owner.username)}`}
          data-testid="public-collection-owner"
          className="inline-flex items-center gap-3 hover:text-[var(--bronze)] group"
        >
          {data.owner.picture ? (
            <img src={data.owner.picture} alt="" className="w-10 h-10 rounded-full object-cover border border-[rgba(140,106,47,0.2)]" />
          ) : (
            <div className="w-10 h-10 rounded-full bg-[var(--paper-aged)] border border-[rgba(140,106,47,0.2)] flex items-center justify-center font-serif-lit text-sm text-[var(--bronze)]">
              {(data.owner.name || "?").slice(0, 1).toUpperCase()}
            </div>
          )}
          <div className="text-left">
            <p className="font-serif-lit text-base text-[var(--ink)] leading-tight group-hover:text-[var(--bronze)]">{data.owner.name}</p>
            <p className="font-mono-lc text-[10px] tracking-[0.14em] text-[var(--ink-dusty)]">@{data.owner.username}</p>
          </div>
        </Link>
      )}

      <header className="text-center max-w-2xl mx-auto pt-6">
        <Fleuron size={26} color="var(--bronze)" className="mx-auto opacity-70" />
        <span className="stamp mt-4 inline-block" style={{ "--stamp-color": "var(--bronze)" }}>
          Shared collection · {data.item_count} item{data.item_count === 1 ? "" : "s"}
        </span>
        <h1 className="mt-5 font-serif-lit text-5xl tracking-tight text-[var(--ink)] leading-[1.05]">{data.name}</h1>
        {data.description && (
          <p className="mt-4 font-garamond italic text-lg text-[var(--ink-dusty)]">{data.description}</p>
        )}
        <Flourish color="var(--bronze)" className="mx-auto mt-6 opacity-80" />
      </header>

      <div className="space-y-14">
        {TYPE_ORDER.concat(["unknown"]).map(type => {
          const list = byType[type];
          if (!list || list.length === 0) return null;
          const meta = TYPE_META[type] || TYPE_META.unknown;
          return (
            <section key={type} className="shelf-section" style={{ "--shelf-bg": `${meta.bg}55`, "--shelf-border": meta.border }} data-testid={`public-shelf-${type}`}>
              <header className="text-center mb-8">
                <h2 className="font-cinzel text-xl tracking-[0.22em]" style={{ color: meta.accent }}>{meta.label}</h2>
                <span className="mt-2 inline-block font-mono-lc text-[10px] tracking-[0.22em] uppercase text-[var(--ink-light)]">
                  {list.length} kept
                </span>
              </header>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-7 sm:gap-9">
                {list.map(it => (
                  <a key={it.id} href={it.source_url || "#"} target="_blank" rel="noreferrer"
                     className="placed-card overflow-hidden block" data-testid={`public-item-${it.id}`}
                     style={{ borderColor: meta.border }}>
                    {it.image_url && <div className="aspect-[4/3] overflow-hidden"><img src={it.image_url} alt="" className="w-full h-full object-cover"/></div>}
                    <div className="p-4">
                      <p className="font-cinzel text-[9px] tracking-[0.22em] uppercase" style={{ color: meta.accent }}>{meta.label}</p>
                      <h3 className="mt-2 font-serif-lit text-lg text-[var(--ink)] line-clamp-2">{it.title || "Untitled"}</h3>
                      {it.creator && <p className="mt-1 font-garamond italic text-sm text-[var(--ink-dusty)] line-clamp-1">{it.creator}</p>}
                      {it.note && <p className="mt-2 font-garamond italic text-xs text-[var(--ink-light)] line-clamp-2">{it.note}</p>}
                    </div>
                  </a>
                ))}
              </div>
            </section>
          );
        })}
        {data.item_count === 0 && (
          <div className="max-w-xl mx-auto ornate-frame text-center">
            <p className="font-serif-lit text-2xl italic text-[var(--ink)]">An empty collection.</p>
          </div>
        )}
      </div>

      <footer className="text-center pt-12 pb-16 border-t border-[rgba(140,106,47,0.18)]">
        <Link to="/" className="font-cinzel text-[10px] tracking-[0.26em] uppercase text-[var(--bronze)] hover:text-[var(--ink)]" data-testid="public-footer-link">
          Make your own at Curated Ethos
        </Link>
      </footer>
    </motion.div>
  );
}
